import {
    forwardRef,
    useEffect,
    useState,
    useRef
} from "react";
import {
    Pressable,
    Platform,
    Animated,
    View
} from "react-native";
import type ITabBarItemProps from "./type";
import type {
    TabBarItemWebPropsType
} from "./type";
import stylesheet, {
    TAB_BAR_ITEM_LABEL_VARIANT,
    getTabBarItemBadgeLocation,
    getTabBarItemColors,
    useStyles
} from "./stylesheet";
import {
    NCoreUIKitLocalize,
    NCoreUIKitTheme
} from "../../../../core/hooks";
import {
    INTERACTION_STATE_DATA_KEY,
    useInteractionState
} from "../../../../helpers/interaction";
import {
    MOTION_ICON_POP_SCALE,
    getMotionTransition,
    useAnimatedState,
    useMotionTiming
} from "../../../../helpers/motion";
import {
    BADGE_INDICATOR_MAX_COUNT,
    getBadgeIndicatorCount
} from "../../../badge/components/indicator/stylesheet";
import {
    formatNumber
} from "../../../../helpers/locale/format";
import {
    webStyle
} from "../../../../utils";
import StateLayer from "../../../stateLayer";
import Badge from "../../../badge";
import Text from "../../../text";

const TabBarItem = forwardRef<View, ITabBarItemProps>(({
    icon: IconComponent,
    isDisabled = false,
    isActive = false,
    customLocalize,
    isOpen = false,
    labelBehaviour,
    iconAnimation,
    customTheme,
    onHoverOut,
    onPressOut,
    onHoverIn,
    onPressIn,
    onFocus,
    onPress,
    onBlur,
    badge,
    title,
    href,
    ...props
}, ref) => {
    const {
        controlHeights,
        shapeConfigs,
        touchTargets,
        typography,
        durations,
        iconSizes,
        radiuses,
        borders,
        colors,
        spaces
    } = NCoreUIKitTheme.useContext(customTheme);

    const {
        activeLocale,
        localize
    } = NCoreUIKitLocalize.useContext(customLocalize);

    const {
        isReduceMotionEnabled,
        duration,
        easing
    } = useMotionTiming("press", customTheme);

    const scale = useRef(new Animated.Value(1)).current;

    const colorProgress = useAnimatedState({
        value: isActive && !isDisabled ? 1 : 0,
        intent: "toggle",
        motion: "fade",
        customTheme
    });

    const [
        isRingActive,
        setIsRingActive
    ] = useState<boolean>(isActive);

    const {
        interactionHandlers,
        isFocusVisible,
        isHovered,
        isPressed
    } = useInteractionState({
        isDisabled,
        onHoverOut,
        onPressOut,
        onHoverIn,
        onPressIn,
        onFocus,
        onBlur
    });

    const isPopup = props["aria-haspopup"] !== undefined && props["aria-haspopup"] !== false;

    const isLink = Platform.OS === "web" && !!href && !isPopup && !isDisabled;

    const currentColors = getTabBarItemColors({
        isDisabled,
        isActive,
        colors
    });

    const activeColors = getTabBarItemColors({
        isActive: true,
        isDisabled,
        colors
    });

    const restingColors = getTabBarItemColors({
        isActive: false,
        isDisabled,
        colors
    });

    const {
        placeholder: placeholderDynamicStyle,
        container: containerDynamicStyle,
        indicator: indicatorDynamicStyle,
        label: labelDynamicStyle
    } = useStyles({
        controlHeights,
        isFocusVisible,
        labelBehaviour,
        shapeConfigs,
        touchTargets,
        isDisabled,
        typography,
        isActive,
        radiuses,
        borders,
        colors,
        spaces
    });

    useEffect(() => {
        return () => {
            scale.stopAnimation();
        };
    }, []);

    useEffect(() => {
        if(isReduceMotionEnabled) {
            setIsRingActive(isActive);

            return;
        }

        const timeout = setTimeout(() => {
            setIsRingActive(isActive);
        }, duration);

        return () => {
            clearTimeout(timeout);
        };
    }, [
        isActive
    ]);

    const getAccessibilityLabel = () => {
        if(badge === "dot") {
            return localize("notification-indicator-new", [
                title
            ]);
        }

        if(badge === undefined || badge <= 0) {
            return title;
        }

        if(badge > BADGE_INDICATOR_MAX_COUNT) {
            return localize("notification-indicator-overflow", [
                title,
                formatNumber({
                    value: BADGE_INDICATOR_MAX_COUNT,
                    locale: activeLocale
                })
            ]);
        }

        return localize("notification-indicator-count", [
            title,
            getBadgeIndicatorCount({
                locale: activeLocale,
                count: badge
            })
        ]);
    };

    const handleIconPop = () => {
        if(iconAnimation !== "signature" || isReduceMotionEnabled) {
            return;
        }

        scale.stopAnimation();

        Animated.sequence([
            Animated.timing(scale, {
                useNativeDriver: Platform.OS !== "web",
                toValue: MOTION_ICON_POP_SCALE,
                isInteraction: false,
                duration,
                easing
            }),
            Animated.timing(scale, {
                useNativeDriver: Platform.OS !== "web",
                isInteraction: false,
                toValue: 1,
                duration,
                easing
            })
        ]).start();
    };

    const handlePress = () => {
        if(isDisabled) {
            return;
        }

        handleIconPop();

        onPress?.();
    };

    const webProps: TabBarItemWebPropsType = Platform.OS === "web" ? {
        "aria-current": isActive && !isPopup ? "page" : undefined,
        dataSet: isDisabled ? {
            [INTERACTION_STATE_DATA_KEY]: "disabled"
        } : interactionHandlers.dataSet,
        href: isLink ? href : undefined
    } : {};

    const labelTransitionStyle = webStyle(getMotionTransition({
        isReduceMotionEnabled,
        intent: "toggle",
        motion: "fade",
        properties: [
            "color"
        ],
        durations
    }));

    const renderIcon = () => {
        if(!IconComponent) {
            return null;
        }

        return <Animated.View
            style={{
                transform: [
                    {
                        scale
                    }
                ]
            }}
        >
            <IconComponent
                customColor={restingColors.iconColor}
                color={restingColors.iconColorKey}
                size={iconSizes.large}
            />
            {
                isDisabled ?
                    null
                :
                    <Animated.View
                        style={[
                            stylesheet.overlay,
                            {
                                opacity: colorProgress
                            }
                        ]}
                        aria-hidden={true}
                    >
                        <IconComponent
                            customColor={activeColors.iconColor}
                            color={activeColors.iconColorKey}
                            size={iconSizes.large}
                        />
                    </Animated.View>
            }
        </Animated.View>;
    };

    const renderLabel = () => {
        if(labelBehaviour === "never") {
            return null;
        }

        if(labelBehaviour === "selected" && !isActive) {
            return <View
                style={placeholderDynamicStyle}
                aria-hidden={true}
            />;
        }

        return <Text
            customColor={currentColors.titleColor}
            variant={TAB_BAR_ITEM_LABEL_VARIANT}
            customTheme={customTheme}
            style={[
                stylesheet.label,
                labelDynamicStyle,
                labelTransitionStyle
            ]}
            aria-hidden={true}
            numberOfLines={1}
        >
            {title}
        </Text>;
    };

    return <Pressable
        {...props}
        {...interactionHandlers}
        {...webProps}
        aria-selected={Platform.OS === "web" || isPopup ? undefined : isActive}
        aria-label={getAccessibilityLabel()}
        role={isPopup ? "button" : "link"}
        aria-disabled={isDisabled}
        style={[
            stylesheet.container,
            containerDynamicStyle
        ]}
        disabled={isDisabled}
        onPress={handlePress}
        ref={ref}
    >
        <View
            style={[
                stylesheet.indicator,
                indicatorDynamicStyle
            ]}
        >
            <StateLayer
                isPressed={!isDisabled && (isPressed || isOpen)}
                isHovered={!isDisabled && isHovered}
                borderRadius={radiuses.actions}
                customTheme={customTheme}
                overlayColor="default"
                borderWidth={0}
            />
            <Badge.Indicator
                floorBackgroundColor={isRingActive && !isDisabled ? "selected" : "mid"}
                count={typeof badge === "number" ? badge : undefined}
                location={getTabBarItemBadgeLocation({
                    iconSizes,
                    badge
                })}
                isVisible={badge !== undefined}
                customLocalize={customLocalize}
                customTheme={customTheme}
                isDisabled={isDisabled}
                spreadBehaviour="free"
                type="danger"
            >
                {renderIcon()}
            </Badge.Indicator>
        </View>
        {renderLabel()}
    </Pressable>;
});
export default TabBarItem;
